import {
  BeaconMessageType,
  type BeaconShareCodeCreatedPayload,
  type BeaconShareCodeReclaimedPayload,
  type BeaconShareCodeReclaimFailedPayload,
  type BeaconPlayerCountUpdatedPayload,
} from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import type { IMessageRouter } from '../../services/MessageRouter';
import type { AppCoordinator } from '../app/AppCoordinator';
import type { SessionCoordinator } from '../session/SessionCoordinator';
import type { PeerCoordinator } from '../peer/PeerCoordinator';
import type { BeaconInputConfig } from './types';
import { ShareCodeManager } from './managers/ShareCodeManager';
import { ConnectionGateManager } from './managers/ConnectionGateManager';
import { createBeaconStores, type BeaconStores } from './stores';
import { CoordinatorEventImpl } from '../_types/events';
import constants from '@playcastdotio/GlobalConfig';

const DEFAULT_MAX_PLAYERS = 4;

export type BeaconCoordinatorDeps = {
  realtimeService: IRealtimeService;
  getMachineId: () => string;
};

export class BeaconCoordinator {
  private deps: BeaconCoordinatorDeps;
  private appCoordinator: AppCoordinator | null = null;
  private sessionCoordinator: SessionCoordinator | null = null;
  private peerCoordinator: PeerCoordinator | null = null;
  private shareCodeManager: ShareCodeManager;
  private connectionGateManager: ConnectionGateManager;
  private stores: BeaconStores;
  private unsubscribers: Array<() => void> = [];
  private authId = '';
  private initialized = false;

  public readonly events = {
    shareCodeReady: new CoordinatorEventImpl<{ shareCode: string; shareLink: string }>(),
    shareCodeEnded: new CoordinatorEventImpl<void>(),
    playerCountChanged: new CoordinatorEventImpl<{ currentPlayers: number; maxPlayers: number }>(),
    inputConfigChanged: new CoordinatorEventImpl<BeaconInputConfig>(),
  };

  constructor(deps: BeaconCoordinatorDeps) {
    this.deps = deps;
    this.stores = createBeaconStores();
    this.connectionGateManager = new ConnectionGateManager(DEFAULT_MAX_PLAYERS);

    this.shareCodeManager = new ShareCodeManager({
      realtimeService: this.deps.realtimeService,
      getMachineId: () => this.deps.getMachineId(),
      getAuthId: () => this.authId,
      getMaxPlayers: () => this.connectionGateManager.getMaxPlayers(),
      getPlayerBaseUrl: () => constants.BEACON_BASE_URL.replace(/\/$/, ''),
      getStreamTitle: () => this.stores.beacon.currentState.streamTitle,
    });

    this.wireManagerEvents();
  }

  init(
    appCoordinator: AppCoordinator,
    sessionCoordinator: SessionCoordinator,
    peerCoordinator: PeerCoordinator,
  ): void {
    if (this.initialized) {
      console.warn('[BeaconCoordinator] Already initialized');
      return;
    }

    this.appCoordinator = appCoordinator;
    this.sessionCoordinator = sessionCoordinator;
    this.peerCoordinator = peerCoordinator;

    this.wireSessionEvents();
    this.wirePeerEvents();
    this.wireReconnect();

    this.initialized = true;
    console.log('[BeaconCoordinator] Initialized');
  }

  registerMessageHandlers(router: IMessageRouter): void {
    router.register(
      BeaconMessageType.BEACON_SHARE_CODE_CREATED,
      this.onShareCodeCreated.bind(this),
    );
    router.register(
      BeaconMessageType.BEACON_SHARE_CODE_RECLAIMED,
      this.onShareCodeReclaimed.bind(this),
    );
    router.register(
      BeaconMessageType.BEACON_SHARE_CODE_RECLAIM_FAILED,
      this.onShareCodeReclaimFailed.bind(this),
    );
    router.register(
      BeaconMessageType.BEACON_SHARE_CODE_ENDED,
      this.onShareCodeEnded.bind(this),
    );
    router.register(
      BeaconMessageType.BEACON_PLAYER_COUNT_UPDATED,
      this.onPlayerCountUpdated.bind(this),
    );
  }

  // ========================================
  // Public API (read)
  // ========================================

  getShareCode(): string | null {
    return this.shareCodeManager.getShareCode();
  }

  getShareLink(): string | null {
    return this.shareCodeManager.getShareLink();
  }

  getPlayerCount(): number {
    return this.connectionGateManager.getCurrentPlayers();
  }

  getMaxPlayers(): number {
    return this.connectionGateManager.getMaxPlayers();
  }

  canAcceptMore(): boolean {
    return this.connectionGateManager.canAcceptMore();
  }

  getInputConfig(): BeaconInputConfig {
    return this.stores.beacon.currentState.inputConfig;
  }

  isPreviewEnabled(): boolean {
    return this.stores.beacon.currentState.previewEnabled;
  }

  getBeaconStore(): BeaconStores['beacon'] {
    return this.stores.beacon;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  // ========================================
  // Public API (actions)
  // ========================================

  updateAuthId(authId: string): void {
    this.authId = authId;
  }

  setTouchEnabled(enabled: boolean): void {
    this.updateInputConfigField('touch', enabled);
  }

  setKeyboardEnabled(enabled: boolean): void {
    this.updateInputConfigField('keyboard', enabled);
  }

  setMouseEnabled(enabled: boolean): void {
    this.updateInputConfigField('mouse', enabled);
  }

  setGamepadEnabled(enabled: boolean): void {
    this.updateInputConfigField('gamepad', enabled);
  }

  setStreamTitle(title: string): void {
    this.stores.beacon.updateState({
      ...this.stores.beacon.currentState,
      streamTitle: title,
    });
  }

  setPreviewEnabled(enabled: boolean): void {
    this.stores.beacon.updateState({
      ...this.stores.beacon.currentState,
      previewEnabled: enabled,
    });
  }

  // ========================================
  // Message handlers
  // ========================================

  private onShareCodeCreated(message: any): void {
    const payload = message as BeaconShareCodeCreatedPayload & { type: string };
    this.shareCodeManager.handleShareCodeCreated(payload);
  }

  private onShareCodeReclaimed(message: any): void {
    const payload = message as BeaconShareCodeReclaimedPayload & { type: string };
    this.shareCodeManager.handleShareCodeReclaimed(payload);
  }

  private onShareCodeReclaimFailed(message: any): void {
    const payload = message as BeaconShareCodeReclaimFailedPayload & { type: string };
    this.shareCodeManager.handleShareCodeReclaimFailed(payload);
  }

  private onShareCodeEnded(_message: any): void {
    console.log('[BeaconCoordinator] Share code ended (server confirmed)');
  }

  private onPlayerCountUpdated(message: any): void {
    const payload = message as BeaconPlayerCountUpdatedPayload & { type: string };
    this.connectionGateManager.onPlayerCountUpdated(payload.currentPlayers);
  }

  // ========================================
  // Internal wiring
  // ========================================

  private wireManagerEvents(): void {
    this.shareCodeManager.events.shareCodeReady.addListener((data) => {
      this.stores.beacon.updateState({
        ...this.stores.beacon.currentState,
        shareCode: data.shareCode,
        shareLink: data.shareLink,
      });
      this.events.shareCodeReady.emit(data);
    });

    this.shareCodeManager.events.shareCodeEnded.addListener(() => {
      this.stores.beacon.updateState({
        ...this.stores.beacon.currentState,
        shareCode: null,
        shareLink: null,
      });
      this.events.shareCodeEnded.emit();
    });

    this.connectionGateManager.events.playerCountChanged.addListener((data) => {
      this.stores.beacon.updateState({
        ...this.stores.beacon.currentState,
        currentPlayers: data.currentPlayers,
        maxPlayers: data.maxPlayers,
      });
      this.events.playerCountChanged.emit(data);
    });
  }

  private wireSessionEvents(): void {
    if (!this.sessionCoordinator) return;

    const unsubStream = this.sessionCoordinator.events.streamStarted.addListener(() => {
      this.shareCodeManager.onStreamStarted();
    });
    this.unsubscribers.push(unsubStream);

    const unsubStop = this.sessionCoordinator.events.streamStopped.addListener(() => {
      this.shareCodeManager.onStreamStopped();
      this.connectionGateManager.reset();
    });
    this.unsubscribers.push(unsubStop);
  }

  private wirePeerEvents(): void {
    if (!this.peerCoordinator) return;

    const unsubJoin = this.peerCoordinator.events.onPlayerJoined.addListener(() => {
      this.connectionGateManager.onPlayerJoined();
    });
    this.unsubscribers.push(unsubJoin);

    const unsubLeave = this.peerCoordinator.events.onPlayerLeft.addListener(() => {
      this.connectionGateManager.onPlayerLeft();
    });
    this.unsubscribers.push(unsubLeave);
  }

  private wireReconnect(): void {
    const unsub = this.deps.realtimeService.onConnected(() => {
      this.shareCodeManager.onReconnected();
    });
    this.unsubscribers.push(unsub);
  }

  private updateInputConfigField(field: keyof BeaconInputConfig, value: boolean): void {
    const current = this.stores.beacon.currentState;
    const updatedConfig: BeaconInputConfig = {
      ...current.inputConfig,
      [field]: value,
    };
    this.stores.beacon.updateState({
      ...current,
      inputConfig: updatedConfig,
    });
    this.events.inputConfigChanged.emit(updatedConfig);
    this.applyPermissionsToAllPeers(updatedConfig);
  }

  private applyPermissionsToAllPeers(config: BeaconInputConfig): void {
    if (!this.peerCoordinator) return;

    const playerPeers = this.peerCoordinator.getPeersByType('player');
    for (const peer of playerPeers) {
      this.peerCoordinator.updatePeerPermissions(peer.peerId, {
        mouse: config.mouse,
        keyboard: config.keyboard,
        touch: config.touch,
        gamepad: config.gamepad,
      });
    }
  }

  destroy(): void {
    this.unsubscribers.forEach((unsub) => unsub());
    this.unsubscribers = [];
    this.initialized = false;
  }
}
